import { Radio } from 'antd'
import { useState } from 'react'

import { Volume } from '@/api/type-server'
import { Button, Input, Modal, Trans } from '@/components'
import { useUserStore } from '@/stores/user'

import { VolumeSelector } from './VolumeSelector'

export function MountDriveModal(props: {
  open: boolean
  mountVolume?: Volume
  mountPath?: string
  readWriteMode?: boolean
  volumes?: Volume[]
  machineDomains?: number[]
  onChange: (volume: Volume, path: string, mode: boolean) => void
  onClose: () => void
}) {
  const {
    open,
    volumes,
    mountVolume: defaultMountVolume,
    mountPath: defaultMountPath,
    readWriteMode: defaultReadWriteMode,
    machineDomains,
    onChange,
    onClose,
  } = props
  const userStore = useUserStore()
  const [mountVolume, setMountVolume] = useState<Volume | undefined>(defaultMountVolume)
  const [mountPath, setMountPath] = useState<string | undefined>(defaultMountPath)
  const [readWriteMode, setReadWriteMode] = useState<boolean>(
    userStore.isOrderExpired ? true : (defaultReadWriteMode ?? false),
  )

  const onMountVolumeSelected = (volume: Volume | undefined) => {
    setMountVolume(volume)
  }

  const onMountPathChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value.trim()

    if (/^[\u4E00-\u9FA5A-Za-z0-9_\-\\.\\/]+$/.test(inputVal)) {
      setMountPath(inputVal)
    } else {
      setMountPath('')
    }
  }

  const onOK = () => {
    onChange && onChange(mountVolume!, mountPath!, readWriteMode)
  }

  return (
    <Modal
      className="w-[800px]"
      open={open}
      onClose={() => {
        onClose && onClose()
      }}
    >
      <Modal.Header>
        <Trans i18nKey="create.mountDriveModalTitle">Mount Data Hub</Trans>
      </Modal.Header>
      <Modal.Body>
        <div className="">
          <VolumeSelector
            values={volumes}
            value={mountVolume}
            machineDomains={machineDomains}
            onChange={onMountVolumeSelected}
          />
          <div className="mt-4">
            <h3 className="text-primary mb-2">
              <Trans i18nKey="create.mountDriveModalMountPath">Mount Path</Trans>
            </h3>
            <Input
              type="text"
              value={mountPath}
              maxLength={30}
              onChange={onMountPathChanged}
              disabled={true}
            />
          </div>
          <div className="mt-4">
            <h3 className="text-primary mb-2">
              <Trans i18nKey="create.mountDriveModalPermissions">Permissions</Trans>
            </h3>
            <Radio.Group
              value={readWriteMode}
              onChange={(e) => setReadWriteMode(e.target.value)}
              disabled={userStore.isOrderExpired}
            >
              <Radio value={true}>
                <Trans i18nKey="create.mountDriveModalPermissionReadOnly">Read only</Trans>
              </Radio>
              <Radio value={false} disabled={userStore.isOrderExpired}>
                <Trans i18nKey="create.mountDriveModalPermissionReadWrite">Read & write</Trans>
              </Radio>
            </Radio.Group>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="outline"
          onClick={() => {
            onClose && onClose()
          }}
        >
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          disabled={!mountVolume || !mountPath || mountPath.length <= 0}
          onClick={onOK}
        >
          Mount
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
